import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, HardHat, MapPin, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  planning: "bg-blue-500",
  active: "bg-orange-600",
  on_hold: "bg-yellow-500",
  completed: "bg-green-600",
};

const empty = {
  name: "", project_type: "NHAI", location: "", contract_value: 0,
  start_date: "", end_date: "", status: "planning", description: "",
};

function fmtINR(n) {
  if (!n) return "₹0";
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return `₹${Number(n).toLocaleString("en-IN")}`;
}

export default function ProjectsPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => api.get("/projects").then((r) => setItems(r.data)), []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const payload = { ...form, contract_value: Number(form.contract_value) || 0 };
      if (editId) await api.put(`/projects/${editId}`, payload);
      else await api.post("/projects", payload);
      toast.success(editId ? "Project updated" : "Project created");
      setOpen(false); setForm(empty); setEditId(null);
      load();
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this project?")) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const edit = (p) => {
    setForm({ ...empty, ...p });
    setEditId(p.id);
    setOpen(true);
  };

  return (
    <div data-testid="projects-page">
      <PageHeader
        overline="OPERATIONS"
        title="Projects"
        description="Road construction projects — NHAI, PWD & Private."
        actions={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditId(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="projects-new-btn" className="bg-orange-600 hover:bg-orange-700 rounded-sm">
                <Plus className="h-4 w-4 mr-2" /> New Project
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading">{editId ? "Edit" : "Create"} Project</DialogTitle>
                <DialogDescription>NHAI, PWD or private road construction project.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="overline">Name</Label>
                  <Input data-testid="project-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="overline">Type</Label>
                    <Select value={form.project_type} onValueChange={(v) => setForm({ ...form, project_type: v })}>
                      <SelectTrigger data-testid="project-form-type" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NHAI">NHAI</SelectItem>
                        <SelectItem value="PWD">PWD</SelectItem>
                        <SelectItem value="Private">Private</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="overline">Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger data-testid="project-form-status" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="planning">Planning</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="on_hold">On hold</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="overline">Location</Label>
                  <Input data-testid="project-form-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="rounded-sm mt-1" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="overline">Contract Value (₹)</Label>
                    <Input data-testid="project-form-value" type="number" value={form.contract_value} onChange={(e) => setForm({ ...form, contract_value: e.target.value })} className="rounded-sm mt-1" />
                  </div>
                  <div>
                    <Label className="overline">Start</Label>
                    <Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="rounded-sm mt-1" />
                  </div>
                  <div>
                    <Label className="overline">End</Label>
                    <Input type="date" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="rounded-sm mt-1" />
                  </div>
                </div>
                <div>
                  <Label className="overline">Description</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm mt-1" rows={3} />
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="project-form-save" onClick={save} className="bg-orange-600 hover:bg-orange-700 rounded-sm">
                  {editId ? "Save changes" : "Create project"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {items.length === 0 ? (
        <div className="border border-dashed border-zinc-300 rounded-sm p-16 text-center bg-white">
          <HardHat className="h-10 w-10 text-zinc-300 mx-auto mb-3" />
          <div className="text-sm text-zinc-500">No projects yet. Create your first project.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((p) => (
            <div key={p.id} data-testid={`project-card-${p.id}`} className="bg-white border border-zinc-200 rounded-sm p-5 hover:border-orange-600 transition-colors group">
              <div className="flex items-start justify-between">
                <Link to={`/projects/${p.id}`} data-testid={`project-open-${p.id}`} className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-mono text-[10px] px-1.5 py-0.5 border border-zinc-300 rounded-sm">{p.project_type}</span>
                    <span className="flex items-center gap-1.5 text-xs text-zinc-600">
                      <span className={`h-1.5 w-1.5 rounded-full ${STATUS_COLORS[p.status]}`} />
                      {p.status}
                    </span>
                  </div>
                  <h3 className="font-heading font-bold text-lg leading-tight group-hover:text-orange-600 transition-colors">{p.name}</h3>
                  <div className="flex items-center gap-1 text-xs text-zinc-500 mt-1">
                    <MapPin className="h-3 w-3" /> {p.location || "—"}
                  </div>
                </Link>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button data-testid={`project-edit-${p.id}`} onClick={() => edit(p)} className="p-1.5 hover:bg-zinc-100 rounded-sm"><Pencil className="h-3.5 w-3.5" /></button>
                  <button data-testid={`project-delete-${p.id}`} onClick={() => remove(p.id)} className="p-1.5 hover:bg-red-50 hover:text-red-600 rounded-sm"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              <Link to={`/projects/${p.id}`} className="block">
                <div className="mt-4 pt-4 border-t border-zinc-100 flex items-center justify-between">
                  <div>
                    <div className="overline">Contract</div>
                    <div className="font-heading font-bold text-lg text-orange-600">{fmtINR(p.contract_value)}</div>
                  </div>
                  {p.start_date && (
                    <div className="text-right">
                      <div className="overline">Timeline</div>
                      <div className="font-mono text-xs">{p.start_date} → {p.end_date || "?"}</div>
                    </div>
                  )}
                </div>
                {p.description && <div className="mt-3 text-xs text-zinc-500 line-clamp-2">{p.description}</div>}
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
